import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  Barcode,
  Copy,
  Pencil,
  User,
  X,
  ArrowRight,
  Flag,
  Hash,
} from 'lucide-react';
import {
  SlimBoxProgress,
  BoxStateBadge,
  EadinStatusBadge,
  StatusBadge,
  ToneBadge,
  VarianceChip,
} from './receivingAtoms';
import ScanCaptureDialog from './ScanCaptureDialog';
import ReceivedBoxesDocsSection from './ReceivedBoxesDocsSection';
import { useReceivingStore } from './ReceivingStore';
import { canCloseReceipt } from './receivingLayout';
import {
  boxesResolved,
  fmtDate,
  fmtDateTime,
  totalExpected,
  totalReceived,
  unitVariance,
  rollupReceivedBySku,
  BOX_STATE_META,
} from './receivingStatus';
import {
  createPoDiscrepancyRequest,
  createPoReceivedBoxesNoteRequest,
  getPoInspectorExtrasRequest,
} from '../../slices/boxReceivedSlice';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { ConfrimModal } from '../../components/ConfrimModal';

function handCountExpDetail(quantity, expected) {
  if (expected == null || Number.isNaN(Number(expected))) return null;
  const delta = Number(quantity) - Number(expected);
  if (delta === 0) return 'matches expected';
  const n = Math.abs(delta);
  const unit = n === 1 ? 'unit' : 'units';
  if (delta < 0) return `${n} ${unit} short of expected ${expected}`;
  return `${n} ${unit} over expected ${expected}`;
}

function Metric({ label, value, emphasize }) {
  return (
    <span>
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</span>{' '}
      <strong className={emphasize || 'text-gray-900'}>{value ?? '—'}</strong>
    </span>
  );
}

function BoxCard({
  po,
  box,
  onHand,
  amend,
  scanCount,
  onScan,
  onRescan,
  onOverride,
  onManual,
  onAmend,
}) {
  const expected = box.expectedUnits;
  const received = box.receivedUnits ?? 0;
  const scanned =
    scanCount?.amountReceived != null
      ? scanCount.amountReceived
      : box.scannedUnits;
  const hand =
    onHand?.quantity ??
    box.handCountUnits ??
    null;
  const amended =
    amend?.amendedQuantity ??
    box.amendedUnits ??
    null;

  const effectiveState =
    hand != null || box.state === 'override_received'
      ? 'override_received'
      : box.duplicateEpcs?.length > 0
        ? 'duplicate_epc'
      : scanned == null || scanned <= 0
        ? 'pending'
        : scanned === expected
          ? 'complete'
          : scanned < expected
            ? 'short'
            : 'over';
  const needsAction = ['short', 'over', 'duplicate_epc'].includes(effectiveState);
  const isResolved = effectiveState === 'complete' || effectiveState === 'override_received';
  // Exp vs Rec: Amended > Hand > Received
  const effectiveReceived = amended ?? hand ?? received;
  // Amend is post-receipt — show when received (inbound/hand/clean-scan) or resolved
  const canAmend =
    po.status !== 'closed' &&
    (isResolved || received > 0 || hand != null || amended != null);
  const varVsReceived = effectiveReceived != null ? effectiveReceived - expected : null;
  const varVsScanned = scanned != null ? scanned - expected : null;

  const line = po.lineItems.find((li) => li.sku === box.sku);
  const handCountDetail = onHand ? handCountExpDetail(onHand.quantity, expected) : null;

  return (
    <div
      className={`relative rounded-md border bg-white p-3 ${
        needsAction ? 'border-amber-300' : 'border-gray-200'
      }`}
    >
      {needsAction && <span className="absolute inset-y-0 left-0 w-1 rounded-l-md bg-amber-500" />}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 w-full">
          <div className="flex items-center justify-between gap-1">
            <span className="text-base font-semibold text-gray-900">Box {box.boxNumber}</span>
            <BoxStateBadge state={effectiveState} />
          </div>
          <span className="truncate text-sm text-gray-500">
            {box.sku} · {line?.productName}
          </span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-base">
        <Metric label="Exp" value={expected} />
        <Metric label="Rec" value={received} />
        <Metric
          label="Scan"
          value={scanned ?? '—'}
          emphasize={scanned === expected ? 'text-green-700' : 'text-gray-900'}
        />
        {hand != null && <Metric label="Hand" value={hand} />}
        {amended != null && <Metric label="Amended" value={amended} />}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-base">
        <span className="flex items-center gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Exp vs Rec
          </span>
          <VarianceChip value={varVsReceived} />
        </span>
        <span className="flex items-center gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Exp vs Scan
          </span>
          <VarianceChip value={varVsScanned} />
        </span>
      </div>
      {box.duplicateEpcs?.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 rounded bg-red-50 px-2 py-1.5 text-sm text-red-700">
          <Hash className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-mono">{box.duplicateEpcs[0].hexEpc}</span>
          <span className="shrink-0">
            already received in {box.duplicateEpcs[0].locationLabel}
            {box.duplicateEpcs.length > 1 ? ` (+${box.duplicateEpcs.length - 1} more)` : ''}
          </span>
        </div>
      )}
      {onHand && (
        <p className="mt-1.5 text-sm text-gray-500">
          {onHand.source === 'manual_receive' ? 'Manual receive' : 'Hand count'} {onHand.quantity}
          {handCountDetail ? ` — ${handCountDetail}.` : ''}
          {onHand.authorName ? ` ${onHand.authorName}` : ''}
          {onHand.createdAt ? ` (${fmtDateTime(onHand.createdAt)})` : ''}
        </p>
      )}
      {amend && (
        <p className="mt-1 text-sm text-gray-500">
          Amended to {amend.amendedQuantity}
          {amend.authorName ? ` by ${amend.authorName}` : ''}
          {amend.reason ? ` — ${amend.reason}` : ''}
        </p>
      )}
      {(scanCount?.createdAt || box.scannedAt) && (
        <p className="mt-1.5 text-sm text-gray-500">
          Scanned {fmtDateTime(scanCount?.createdAt || box.scannedAt)}
          {scanCount?.packerName || box.scannedBy
            ? ` by ${scanCount?.packerName || box.scannedBy}`
            : ''}
        </p>
      )}
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {needsAction && (
          <>
            <button
              type="button"
              onClick={() => onRescan(box)}
              className="inline-flex h-7 items-center gap-1 rounded-md bg-gray-900 px-2.5 text-sm font-medium text-white hover:bg-gray-800"
            >
              <Barcode className="h-3.5 w-3.5" /> Re-scan box
            </button>
            <button
              type="button"
              onClick={() => onOverride(box)}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 text-sm font-medium hover:bg-gray-50"
            >
              Enter hand count
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        {(effectiveState === 'pending' || effectiveState === 'scanning') && (
          <>
            <button
              type="button"
              onClick={() => onScan(box)}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 text-sm font-medium hover:bg-gray-50"
            >
              <Barcode className="h-3.5 w-3.5" /> Start RFID scan
            </button>
            {/* Manual receive only when not yet received and no scan on record */}
            {received <= 0 && (scanned == null || scanned <= 0) && (
              <button
                type="button"
                onClick={() => onManual(box)}
                className="inline-flex h-7 items-center px-2.5 text-sm text-gray-500 hover:text-gray-800"
              >
                Manual receive
              </button>
            )}
          </>
        )}
        {canAmend && (
          <button
            type="button"
            onClick={() => onAmend(box)}
            className="inline-flex h-7 items-center gap-1 px-2.5 text-sm text-gray-500 hover:text-gray-800"
          >
            <Pencil className="h-3.5 w-3.5" /> Amend count
          </button>
        )}
      </div>
    </div>
  );
}

export function InspectorEmpty() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
      <p className="text-base font-semibold text-gray-900">No receipt selected</p>
      <p className="max-w-[240px] text-sm leading-snug text-gray-500">
        Select a PO in the queue to inspect boxes, resolve variances, and take receiving actions.
      </p>
    </div>
  );
}

function PoInspector({ poId, onClose }) {
  const reduxDispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const canClose = canCloseReceipt(currentUser);
  const {
    pos,
    captureScan,
    captureAmendScan,
    closeReceipt,
    reconcile,
    overrideReceiveBox,
    manualReceiveBox,
    amendBoxUnits,
    ensurePoLoaded,
  } = useReceivingStore();
  const logsLoading = useSelector((state) => state.boxReceived.logsLoading);
  const po = useMemo(() => pos.find((p) => p.id === poId) ?? null, [pos, poId]);
  const receivedBySku = useMemo(
    () => (po?.boxes?.length ? rollupReceivedBySku(po.boxes) : new Map()),
    [po?.boxes],
  );
  const inspectorExtras = useSelector((state) =>
    po?.poDbId ? state.boxReceived.inspectorByPoId[po.poDbId] : null,
  );
  const notes = inspectorExtras?.notes || [];
  const discrepancies = inspectorExtras?.discrepancies || [];
  const notesLoading = inspectorExtras?.loading || false;
  const docsCount = (inspectorExtras?.files || []).length;
  const onHandCounts = inspectorExtras?.onHandCounts || [];
  const amendCounts = inspectorExtras?.amendCounts || [];
  const boxScanCounts = inspectorExtras?.boxScanCounts || [];
  const activityFeed = inspectorExtras?.activity || [];

  const onHandByBoxId = useMemo(() => {
    const map = new Map();
    for (const row of onHandCounts) {
      const key = String(row.boxId);
      if (!map.has(key)) map.set(key, row);
    }
    return map;
  }, [onHandCounts]);

  const amendByBoxId = useMemo(() => {
    const map = new Map();
    for (const row of amendCounts) {
      const key = String(row.boxId);
      if (!map.has(key)) map.set(key, row);
    }
    return map;
  }, [amendCounts]);

  const scanByBoxId = useMemo(() => {
    const map = new Map();
    for (const row of boxScanCounts) {
      const key = String(row.boxId);
      if (!map.has(key)) map.set(key, row);
    }
    return map;
  }, [boxScanCounts]);

  const [tab, setTab] = useState('boxes');
  const [captureBox, setCaptureBox] = useState(null);
  const [captureKind, setCaptureKind] = useState('initial');
  const [overrideBox, setOverrideBox] = useState(null);
  const [handCount, setHandCount] = useState('');
  const [handConfirmed, setHandConfirmed] = useState(false);
  const [manualBox, setManualBox] = useState(null);
  const [manualCount, setManualCount] = useState('');
  const [manualConfirmed, setManualConfirmed] = useState(false);
  const [amendBox, setAmendBox] = useState(null);
  const [amendCount, setAmendCount] = useState('');
  const [amendReason, setAmendReason] = useState('');
  const [amendMode, setAmendMode] = useState('manual');
  const [noteText, setNoteText] = useState('');
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagText, setFlagText] = useState('');
  const [flagSaving, setFlagSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const inspectorLoadRef = useRef({ poId: null, promise: null });

  useEffect(() => {
    let cancelled = false;
    const loadPo = async () => {
      if (!poId) return;
      try {
        if (inspectorLoadRef.current.poId !== poId) {
          inspectorLoadRef.current = { poId, promise: ensurePoLoaded(poId) };
        }
        await inspectorLoadRef.current.promise;
      } catch (error) {
        if (!cancelled) console.error('Failed to load PO for inspector', error);
      } finally {
        if (inspectorLoadRef.current.poId === poId) {
          inspectorLoadRef.current = { poId: null, promise: null };
        }
      }
    };
    loadPo();
    return () => {
      cancelled = true;
    };
  }, [ensurePoLoaded, poId]);

  useEffect(() => {
    if (!po?.poDbId) return undefined;

    let cancelled = false;
    const loadExtras = async () => {
      try {
        await reduxDispatch(
          getPoInspectorExtrasRequest({ purchaseOrderId: po.poDbId }),
        ).unwrap();
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load PO inspector extras', error);
        toast.error('Failed to load notes / discrepancies', { position: 'bottom-right' });
      }
    };

    loadExtras();
    return () => {
      cancelled = true;
    };
  }, [po?.poDbId, reduxDispatch]);

  const openExceptions = useMemo(() => {
    const flagged = discrepancies.map((d) => ({
      id: `disc-${d.id}`,
      kind: 'manual_flag',
      detail: d.description,
      slackPushed: true,
      resolved: false,
    }));
    return [...flagged];
  }, [po?.exceptions, discrepancies]);

  if (!po) return <InspectorEmpty />;

  const { resolved, total } = boxesResolved(po);
  const expected = totalExpected(po);
  const received = totalReceived(po);
  const variance = unitVariance(po);

  const submitFlag = async () => {
    if (!flagText.trim() || !po.poDbId) {
      if (!po.poDbId) toast.error('Purchase order not loaded yet', { position: 'bottom-right' });
      return;
    }
    setFlagSaving(true);
    try {
      await reduxDispatch(
        createPoDiscrepancyRequest({
          purchaseOrderId: po.poDbId,
          description: flagText.trim(),
        }),
      ).unwrap();
      setFlagOpen(false);
      setFlagText('');
      toast.success('Discrepancy flagged', { position: 'bottom-right' });
    } catch (error) {
      console.error('Failed to flag discrepancy', error);
      toast.error('Failed to flag discrepancy', { position: 'bottom-right' });
    } finally {
      setFlagSaving(false);
    }
  };

  const submitNote = async () => {
    if (!noteText.trim() || !po.poDbId) {
      if (!po.poDbId) toast.error('Purchase order not loaded yet', { position: 'bottom-right' });
      return;
    }
    try {
      await reduxDispatch(
        createPoReceivedBoxesNoteRequest({
          purchaseOrderId: po.poDbId,
          description: noteText.trim(),
        }),
      ).unwrap();
      setNoteText('');
    } catch (error) {
      console.error('Failed to add note', error);
      toast.error('Failed to add note', { position: 'bottom-right' });
    }
  };

  const currentReceivedUnits = (box) =>
    box.amendedUnits ??
    box.handCountUnits ??
    box.receivedUnits ??
    0;

  const openAmend = (box) => {
    setAmendBox(box);
    setAmendCount(String(currentReceivedUnits(box)));
    setAmendReason('');
    setAmendMode('manual');
  };

  const submitOverride = async () => {
    if (!overrideBox) return;
    const count = parseInt(handCount, 10);
    if (Number.isNaN(count) || count < 0) {
      toast.error('Enter the actual physical count first.', { position: 'bottom-right' });
      return;
    }
    if (!handConfirmed) {
      toast.error('Confirm that you physically counted this box.', { position: 'bottom-right' });
      return;
    }
    try {
      setSaving(true);
      await overrideReceiveBox(po.id, overrideBox.id, count);
      setOverrideBox(null);
      setHandConfirmed(false);
      toast.success(`Box ${overrideBox.boxNumber} received via override (${count} units)`, {
        position: 'bottom-right',
      });
    } catch (error) {
      console.error('Failed to save hand count', error);
      toast.error(error?.error || error?.message || 'Failed to save hand count', {
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const submitManual = async () => {
    if (!manualBox) return;
    const count = parseInt(manualCount, 10);
    if (Number.isNaN(count) || count < 0) {
      toast.error('Enter the actual unit count for this box first.', { position: 'bottom-right' });
      return;
    }
    if (!manualConfirmed) {
      toast.error('Confirm that you physically counted this box.', { position: 'bottom-right' });
      return;
    }
    try {
      setSaving(true);
      await manualReceiveBox(po.id, manualBox.id, count);
      const delta = count - manualBox.expectedUnits;
      setManualBox(null);
      setManualConfirmed(false);
      toast.success(
        delta === 0
          ? `Box ${manualBox.boxNumber} manually received (${count} units)`
          : `Box ${manualBox.boxNumber} manually received with variance ${delta > 0 ? '+' : ''}${delta}`,
        { position: 'bottom-right' },
      );
    } catch (error) {
      console.error('Failed to save manual receive', error);
      toast.error(error?.error || error?.message || 'Failed to save manual receive', {
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const submitAmend = async () => {
    if (!amendBox) return;
    const count = parseInt(amendCount, 10);
    if (Number.isNaN(count) || count < 0) {
      toast.error('Enter the corrected unit count first.', { position: 'bottom-right' });
      return;
    }
    if (!amendReason.trim()) {
      toast.error('A reason is required for post-receive amendments.', { position: 'bottom-right' });
      return;
    }
    const prev = currentReceivedUnits(amendBox);
    if (count === prev) {
      toast.error('The corrected count matches the current count — nothing to amend.', {
        position: 'bottom-right',
      });
      return;
    }
    try {
      setSaving(true);
      await amendBoxUnits(po.id, amendBox.id, count, amendReason.trim());
      setAmendBox(null);
      toast.success(`Box ${amendBox.boxNumber} amended (${prev} → ${count} units)`, {
        position: 'bottom-right',
      });
    } catch (error) {
      console.error('Failed to save amendment', error);
      toast.error(error?.error || error?.message || 'Failed to save amendment', {
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const submitAmendRescan = () => {
    if (!amendBox) return;
    setCaptureKind('verify');
    setCaptureBox(amendBox);
    setAmendBox(null);
  };

  const submitCapture = async (scanPayload) => {
    if (!captureBox) return;
    setSaving(true);
    const epcs = scanPayload?.epcs || [];
    const found = scanPayload?.matchedCount ?? new Set(epcs).size;
    const exp = captureBox.expectedUnits;
    const boxNum = captureBox.boxNumber;
    try {
      if (captureKind === 'verify') {
        await captureAmendScan(po.id, captureBox.id, scanPayload);
        toast.success(
          found === exp
            ? `Box ${boxNum} verification re-scan: ${found}/${exp} — count confirmed`
            : `Box ${boxNum} verification re-scan: amended to ${found}/${exp}`,
        { position: 'bottom-right' });
      } else {
        await captureScan(po.id, captureBox.id, scanPayload, captureKind);
        if (found === exp) {
          toast.success(
            captureKind === 'rescan'
              ? `Box ${boxNum} re-scanned ${found}/${exp} — auto-received`
              : `Box ${boxNum} scanned ${found}/${exp} — auto-received`,
          { position: 'bottom-right' });
        } else {
          toast.warn(
            `Box ${boxNum} ${captureKind === 'rescan' ? 're-scanned' : 'scanned'} ${found}/${exp} — ${found < exp ? 'SHORT' : 'OVER'}. Hand-count required.`,
          { position: 'bottom-right' });
        }
      }
      setCaptureBox(null);
      if (scanPayload?.duplicates?.length) {
        const d = scanPayload.duplicates[0];
        toast.warn(
          `Duplicate EPC ${d.hexEpc} — already received in ${d.locationLabel}`,
          { position: 'bottom-right' },
        );
      }
    } catch (err) {
      toast.error(err?.error || err?.message || err?.response?.data?.error || 'Failed to process scan', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'boxes', label: `Boxes (${po.boxes.length})` },
    { key: 'lines', label: `Lines (${po.lineItems.length})` },
    { key: 'docs', label: `Docs (${docsCount})` },
    { key: 'notes', label: `Notes (${notes.length})` },
    { key: 'activity', label: `Activity (${activityFeed.length || (po.activity || []).length})` },
  ];

  // Only block when the PO itself has no box/line data yet — background log
  // refreshes must not blank an inspector that is already showing content.
  if (logsLoading && !po.boxes.length && !po.lineItems.length) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <LoaderSpinner height='90' width='90' border='4' colorSpinner='black' />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 border-b border-gray-200 bg-white px-4 pb-3 pt-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="font-mono text-base font-bold">{po.id}</span>
            <EadinStatusBadge status={po.eadinStatus} />
            {/* {po.eadinStatus !== 'closed' && po.eadinStatus !== 'canceled' && (
              <StatusBadge status={po.status} />
            )}
            {openExceptions.length > 0 && (
              <ToneBadge tone="danger">
                <AlertTriangle className="h-3 w-3" />
                {openExceptions.length}
              </ToneBadge>
            )} */}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              title="Copy PO reference"
              onClick={() => {
                navigator.clipboard.writeText(po.id);
                toast.info('PO reference copied', { position: 'bottom-right' });
              }}
              className="rounded p-1 text-gray-400 hover:bg-gray-100"
            >
              <Copy className="h-4 w-4" />
            </button>
            <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p className="mt-0.5 truncate text-sm text-gray-500">
          {po.supplier}
          {po.asnRef ? ` · ${po.asnRef}` : ''}
        </p>

        <div className="mt-2.5 grid grid-cols-4 gap-px overflow-hidden rounded-md border border-gray-200 bg-gray-200">
          {[
            { label: 'Boxes', value: `${resolved}/${total}` },
            { label: 'Expected', value: expected.toLocaleString() },
            { label: 'Received', value: received.toLocaleString() },
            {
              label: 'Variance',
              value: variance === null ? '—' : variance > 0 ? `+${variance}` : `${variance}`,
            },
          ].map((m) => (
            <div key={m.label} className="bg-white px-2 py-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{m.label}</div>
              <div className="mt-0.5 text-base font-semibold tabular-nums">{m.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-3 mr-2">
            <span className='whitespace-nowrap'>
              Due <span className="font-medium text-gray-800">{fmtDate(po.expectedDate)}</span>
            </span>
            <span className='whitespace-nowrap'>WH <span className="font-medium text-gray-800">{po.warehouse}</span></span>
          </div>
          <span className="flex items-center gap-1">
            <SlimBoxProgress boxes={po.boxes} className="!w-full max-w-[220px]" />
            <span className="text-sm tabular-nums text-gray-500">{resolved}/{total}</span>
          </span>
        </div>

        {canClose && po.eadinStatus !== 'closed' && (
          <button
            type="button"
            onClick={() => setCloseConfirmOpen(true)}
            className="mt-2.5 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
          >
            Close receipt
          </button>
        )}
      </div>

      {/* {openExceptions.length > 0 && (
        <div className="shrink-0 border-b border-red-100 bg-red-50 px-4 py-2">
          {openExceptions.map((e, i) => (
            <div key={e.id || i} className="flex items-start gap-2 py-0.5 text-sm">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
              <span className="text-gray-900">{e.detail}</span>
              <div className="ml-auto shrink-0">
                {e.slackPushed && (
                  <div className="flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="size-3 text-gray-500"
                    >
                      <rect width="3" height="8" x="13" y="2" rx="1.5" />
                      <path d="M19 8.5V10h1.5A1.5 1.5 0 1 0 19 8.5" />
                      <rect width="3" height="8" x="8" y="14" rx="1.5" />
                      <path d="M5 15.5V14H3.5A1.5 1.5 0 1 0 5 15.5" />
                      <rect width="8" height="3" x="14" y="13" rx="1.5" />
                      <path d="M15.5 19H14v1.5a1.5 1.5 0 1 0 1.5-1.5" />
                      <rect width="8" height="3" x="2" y="8" rx="1.5" />
                      <path d="M8.5 5H10V3.5A1.5 1.5 0 1 0 8.5 5" />
                    </svg>
                    <span className="ml-auto shrink-0 text-sm text-gray-500">#fulfillment</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )} */}

      <div className="flex shrink-0 gap-0 border-b border-gray-200 bg-white px-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-2.5 py-2 text-sm font-medium ${
              tab === t.key
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {tab === 'boxes' && (
          <div className="space-y-2">
            <p className="text-sm leading-relaxed text-gray-500">
              Each box resolves independently. Short or over scans require a re-scan first; if that
              still fails, enter a hand count before the box can be received.
            </p>
            {po.boxes.map((box) => (
              <BoxCard
                key={box.id}
                po={po}
                box={box}
                onHand={onHandByBoxId.get(String(box.id)) || null}
                amend={amendByBoxId.get(String(box.id)) || null}
                scanCount={scanByBoxId.get(String(box.id)) || null}
                onScan={(b) => {
                  setCaptureKind('initial');
                  setCaptureBox(b);
                }}
                onRescan={(b) => {
                  setCaptureKind('rescan');
                  setCaptureBox(b);
                }}
                onOverride={(b) => {
                  setOverrideBox(b);
                  setHandCount('');
                  setHandConfirmed(false);
                }}
                onManual={(b) => {
                  setManualBox(b);
                  setManualCount('');
                  setManualConfirmed(false);
                }}
                onAmend={openAmend}
              />
            ))}
          </div>
        )}
        {tab === 'lines' && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <th className="pb-2">SKU / Product</th>
                  <th className="pb-2 text-right">Exp</th>
                  <th className="pb-2 text-right">Recv</th>
                  <th className="pb-2 text-right">Var</th>
                </tr>
              </thead>
              <tbody>
                {po.lineItems.map((li) => {
                  const lineReceived = receivedBySku.get(li.sku) ?? li.receivedUnits ?? 0;
                  const lineVar = lineReceived - (li.expectedUnits || 0);
                  return (
                    <tr key={li.sku} className="border-t border-gray-100">
                      <td className="py-2">
                        <div className="font-medium">{li.productName}</div>
                        <div className="font-mono text-sm text-gray-500">
                          {li.sku}
                          {li.cartonQty ? ` · ${li.cartonQty}/box` : ''}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">{li.expectedUnits}</td>
                      <td className="py-2 text-right tabular-nums">{lineReceived}</td>
                      <td className="py-2 text-right">
                        <VarianceChip value={lineVar} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="h-px w-full bg-gray-100" />
            <p className="mt-2 text-base font-semibold leading-relaxed text-gray-500">
              Scan sessions
            </p>
            {(po.scanSessions || []).length === 0 ? (
              <p className="mt-1.5 text-sm text-gray-400">No scan sessions yet.</p>
            ) : (
              <table className="mt-1.5 h-full w-full min-w-0 border-separate border-spacing-y-2 border-spacing-x-0 bg-white text-base tabular-nums">
                <tbody>
                  {[...(po.scanSessions || [])]
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((sess) => {
                      const box = (po.boxes || []).find((b) => String(b.id) === String(sess.boxId));
                      const result = sess.result || 'complete';
                      const tone =
                        result === 'complete' ? 'ok' : result === 'duplicate_epc' ? 'danger' : 'warn';
                      const matchTone =
                        result === 'complete'
                          ? 'text-green-700'
                          : result === 'duplicate_epc'
                            ? 'text-red-700'
                            : 'text-amber-700';
                      const unitLabel = box
                        ? box.inboundType === 'container'
                          ? `Container ${box.boxNumber}`
                          : `Box ${box.boxNumber}`
                        : null;
                      return (
                        <tr
                          key={sess.id}
                          className={`border border-gray-200 ${
                            sess.superseded ? 'bg-gray-50 opacity-60' : ''
                          }`}
                        >
                          <td className="border-y border-l border-b border-gray-200 rounded-l-md px-2.5 py-1.5 text-gray-500">
                            {fmtDateTime(sess.timestamp)}
                          </td>
                          <td className="border-y border-b border-gray-200 px-2.5 py-1.5 text-gray-600">
                            {unitLabel
                              ? `${unitLabel}${box?.sku ? ` · ${box.sku}` : ''}`
                              : '—'}
                          </td>
                          <td className="border-y border-b border-gray-200 whitespace-nowrap px-2.5 py-1.5">
                            raw <span className="font-semibold">{(sess.rawReads || 0).toLocaleString()}</span>
                          </td>
                          <td className="border-y border-b border-gray-200 whitespace-nowrap px-2.5 py-1.5">
                            uniq{' '}
                            <span className="font-semibold">
                              {(sess.uniqueEpcs ?? sess.matched ?? 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="border-y border-b border-gray-200 whitespace-nowrap px-2.5 py-1.5">
                            match{' '}
                            <span className={`font-semibold ${matchTone}`}>
                              {(sess.matched || 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="border-y border-b border-r border-gray-200 rounded-r-md px-2.5 py-1.5 text-right">
                            <ToneBadge tone={tone}>
                              {(BOX_STATE_META[result]?.label || result).toLowerCase()}
                            </ToneBadge>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </>
        )}
        {tab === 'docs' && (
          <ReceivedBoxesDocsSection purchaseOrderId={po.poDbId} />
        )}
        {tab === 'notes' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note…"
                className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && noteText.trim()) submitNote();
                }}
              />
              <button
                type="button"
                disabled={!noteText.trim() || !po.poDbId}
                onClick={submitNote}
                className="rounded bg-gray-900 px-2 py-1.5 text-sm text-white disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {notesLoading ? (
              <p className="text-sm text-gray-500">Loading notes…</p>
            ) : notes.length === 0 ? (
              <p className="text-sm text-gray-500">No notes yet.</p>
            ) : (
              notes.map((n) => (
                <div key={n.id} className="rounded border border-gray-100 p-2 text-sm">
                  <p>{n.description}</p>
                  <p className="mt-1 text-gray-400">
                    {n.authorName} · {fmtDateTime(n.createdAt)}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
        {tab === 'activity' && (
          <div className="p-1">
            {notesLoading && activityFeed.length === 0 ? (
              <p className="text-sm text-gray-500">Loading activity…</p>
            ) : activityFeed.length === 0 && !(po.activity || []).length ? (
              <p className="text-sm text-gray-500">No activity yet.</p>
            ) : (
              <ol className="relative space-y-0 border-l border-gray-200 pl-4">
                {(activityFeed.length > 0
                  ? activityFeed
                  : [...(po.activity || [])]
                      .map((a, i) => ({
                        id: `local-${i}`,
                        kind: a.kind || 'note',
                        action: a.summary || a.action,
                        detail: a.detail || null,
                        actor: a.user || a.actor || 'Unknown',
                        timestamp: a.timestamp,
                      }))
                      .reverse()
                ).map((e) => (
                  <li key={e.id} className="relative pb-3.5">
                    <span
                      className={`absolute -left-[21.5px] top-[5px] h-[9px] w-[9px] rounded-full border-2 border-white ${
                        e.kind === 'exception'
                          ? 'bg-red-600'
                          : e.kind === 'override' || e.kind === 'amend'
                            ? 'bg-amber-500'
                            : e.kind === 'slack' || e.kind === 'note' || e.kind === 'doc'
                              ? 'bg-blue-500'
                              : e.kind === 'scan' || e.kind === 'receive'
                                ? 'bg-green-600'
                                : 'bg-gray-400'
                      }`}
                    />
                    <div className="text-sm font-medium leading-tight text-gray-900">
                      {e.action}
                    </div>
                    {e.detail && (
                      <div className="text-sm text-gray-500">{e.detail}</div>
                    )}
                    <div className="mt-0.5 text-sm text-gray-400">
                      {e.actor} · {fmtDateTime(e.timestamp)}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>

      {/* RFID scan / re-scan / verification — ScanCaptureDialog */}
      <ScanCaptureDialog
        box={captureBox}
        poDbId={po.poDbId}
        kind={captureKind}
        onClose={() => !saving && setCaptureBox(null)}
        onSubmit={submitCapture}
      />

      {/* Gate 3: hand-count after short/over scan */}
      {overrideBox && (
        <Modal
          title={`Hand-count required — Box ${overrideBox.boxNumber}`}
          widthClassName="max-w-[440px]"
          onClose={() => {
            setOverrideBox(null);
            setHandConfirmed(false);
          }}
        >
          <p className="text-base leading-snug text-gray-600">
            This box scanned{' '}
            <span className="font-semibold tabular-nums text-gray-900">
              {overrideBox.scannedUnits ?? '—'}
            </span>{' '}
            against{' '}
            <span className="font-semibold tabular-nums text-gray-900">
              {overrideBox.expectedUnits}
            </span>{' '}
            expected. Short and over scans never auto-approve.{' '}
            <span className="font-semibold text-gray-900">
              You should have {overrideBox.expectedUnits} total units in this box. Did you count
              them?
            </span>
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400">
                Actual physical count
              </label>
              <input
                type="number"
                min="0"
                autoFocus
                value={handCount}
                onChange={(e) => setHandCount(e.target.value)}
                className="h-9 w-full rounded border border-gray-300 px-3 text-base tabular-nums"
                placeholder={`e.g. ${overrideBox.expectedUnits}`}
              />
            </div>
            {handCount !== '' &&
              !Number.isNaN(parseInt(handCount, 10)) &&
              overrideBox.scannedUnits != null &&
              parseInt(handCount, 10) !== overrideBox.scannedUnits && (
                <p className="rounded bg-blue-50 px-2.5 py-1.5 text-sm text-blue-800">
                  Physical count differs from the scan by{' '}
                  {Math.abs(parseInt(handCount, 10) - overrideBox.scannedUnits)} — likely misread
                  or damaged tag(s). The hand count becomes the received quantity.
                </p>
              )}
            <label className="flex cursor-pointer items-start gap-2 text-base leading-snug text-gray-700">
              <input
                type="checkbox"
                checked={handConfirmed}
                onChange={(e) => setHandConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
              />
              <span>
                I physically opened and counted this box. I understand this override is logged to{' '}
                <span className="font-medium">#fulfillment</span>.
              </span>
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setOverrideBox(null);
                setHandConfirmed(false);
              }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-base hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving || !handConfirmed || handCount === ''}
              onClick={submitOverride}
              className="rounded-md bg-gray-900 px-3 py-1.5 text-base font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              Confirm hand count &amp; receive
            </button>
          </div>
        </Modal>
      )}

      {/* Gate 4: manual receive without scan */}
      {manualBox && (
        <Modal
          title={
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              No RFID scan on record
            </span>
          }
          widthClassName="max-w-[540px]"
          onClose={() => {
            setManualBox(null);
            setManualConfirmed(false);
          }}
        >
          <p className="text-base leading-snug text-gray-600">
            I&apos;m not seeing an RFID scan on Box {manualBox.boxNumber} (
            {manualBox.expectedUnits} units expected). Every unit carries a unique EPC, so a
            missing scan is always meaningful.{' '}
            <span className="font-semibold text-gray-900">
              Count the box by hand before receiving it.
            </span>
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400">
                Actual units in this box
              </label>
              <input
                type="number"
                min="0"
                autoFocus
                value={manualCount}
                onChange={(e) => setManualCount(e.target.value)}
                className="h-9 w-full rounded border border-gray-300 px-3 text-base tabular-nums"
                placeholder={`e.g. ${manualBox.expectedUnits}`}
              />
            </div>
            {manualCount !== '' &&
              !Number.isNaN(parseInt(manualCount, 10)) &&
              parseInt(manualCount, 10) !== manualBox.expectedUnits && (
                <p className="rounded bg-amber-50 px-2.5 py-1.5 text-sm text-amber-800">
                  Count differs from the {manualBox.expectedUnits} expected by{' '}
                  {parseInt(manualCount, 10) - manualBox.expectedUnits > 0 ? '+' : ''}
                  {parseInt(manualCount, 10) - manualBox.expectedUnits}. A variance exception will
                  be raised and pushed to #fulfillment.
                </p>
              )}
            <label className="flex cursor-pointer items-start gap-2 text-base leading-snug text-gray-700">
              <input
                type="checkbox"
                checked={manualConfirmed}
                onChange={(e) => setManualConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
              />
              <span>
                I physically counted this box. I understand this no-scan receive is logged to{' '}
                <span className="font-medium">#fulfillment</span>.
              </span>
            </label>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setManualBox(null);
                setManualConfirmed(false);
              }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-base hover:bg-gray-50"
            >
              Cancel — scan it first
            </button>
            <button
              type="button"
              disabled={saving || !manualConfirmed || manualCount === ''}
              onClick={submitManual}
              className="rounded-md bg-red-700 px-3 py-1.5 text-base font-medium text-white hover:bg-red-800 disabled:opacity-50"
            >
              Manually receive without scan
            </button>
          </div>
        </Modal>
      )}

      {/* Post-receive amendment (manual count or verification re-scan) */}
      {amendBox && (
        <Modal
          title={
            <span className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-gray-500" />
              Amend received count
            </span>
          }
          widthClassName="max-w-[540px]"
          onClose={() => setAmendBox(null)}
        >
          <p className="text-base leading-snug text-gray-600">
            Box {amendBox.boxNumber} is already received with {currentReceivedUnits(amendBox)}{' '}
            units on record. Corrections don&apos;t rewrite history — the original receipt stays
            in the activity trail and the amendment is{' '}
            <span className="font-semibold text-gray-900">
              logged with your name and reason.
            </span>
          </p>
          <div className="mt-4 grid grid-cols-2 gap-1 rounded-md border border-gray-200 bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setAmendMode('manual')}
              className={`rounded px-2 py-1.5 text-sm font-medium transition-colors ${
                amendMode === 'manual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Pencil className="h-3.5 w-3.5" />
                Manual count
              </span>
            </button>
            <button
              type="button"
              onClick={() => setAmendMode('rescan')}
              className={`rounded px-2 py-1.5 text-sm font-medium transition-colors ${
                amendMode === 'rescan'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Barcode className="h-3.5 w-3.5" />
                Verify with re-scan
              </span>
            </button>
          </div>
          <div className="mt-3 space-y-3">
            {amendMode === 'manual' ? (
              <>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Corrected unit count
                  </label>
                  <input
                    type="number"
                    min="0"
                    autoFocus
                    value={amendCount}
                    onChange={(e) => setAmendCount(e.target.value)}
                    className="h-9 w-full rounded border border-gray-300 px-3 text-base tabular-nums"
                    placeholder={`e.g. ${amendBox.expectedUnits}`}
                  />
                </div>
                {amendCount !== '' &&
                  !Number.isNaN(parseInt(amendCount, 10)) &&
                  parseInt(amendCount, 10) !== amendBox.expectedUnits && (
                    <p className="rounded bg-amber-50 px-2.5 py-1.5 text-sm text-amber-800">
                      Corrected count differs from the {amendBox.expectedUnits} expected by{' '}
                      {parseInt(amendCount, 10) - amendBox.expectedUnits > 0 ? '+' : ''}
                      {parseInt(amendCount, 10) - amendBox.expectedUnits}. A variance exception
                      will be raised.
                    </p>
                  )}
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Reason for amendment
                  </label>
                  <input
                    type="text"
                    value={amendReason}
                    onChange={(e) => setAmendReason(e.target.value)}
                    className="h-9 w-full rounded border border-gray-300 px-3 text-base"
                    placeholder="e.g. Found 3 damaged units during putaway"
                  />
                </div>
              </>
            ) : (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm leading-relaxed text-gray-600">
                <p>
                  Re-run the RFID reader over Box {amendBox.boxNumber} and the count amends itself
                  from what the reader actually finds — no typing, no guesswork.
                </p>
                <p className="mt-1.5">
                  The new session supersedes prior scans in the Scan Log (kept for audit), and the
                  amendment is logged as{' '}
                  <span className="font-medium text-gray-900">“Verification re-scan”</span> with
                  your name.
                </p>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setAmendBox(null)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-base hover:bg-gray-50"
            >
              Cancel
            </button>
            {amendMode === 'manual' ? (
              <button
                type="button"
                disabled={saving || amendCount === '' || !amendReason.trim()}
                onClick={submitAmend}
                className="rounded-md bg-gray-900 px-3 py-1.5 text-base font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                Save amendment
              </button>
            ) : (
              <button
                type="button"
                onClick={submitAmendRescan}
                className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-base font-medium text-white hover:bg-gray-800"
              >
                <Barcode className="h-4 w-4" />
                Start verification re-scan
              </button>
            )}
          </div>
        </Modal>
      )}

      {flagOpen && (
        <Modal title="Flag a discrepancy" widthClassName="max-w-[420px]" onClose={() => !flagSaving && setFlagOpen(false)}>
          <p className="mb-3 text-base text-gray-600">
            Shortage, damage, or anything that needs follow-up. Pushed to #fulfillment.
          </p>
          <textarea
            value={flagText}
            onChange={(e) => setFlagText(e.target.value)}
            className="min-h-[70px] w-full rounded border border-gray-300 px-3 py-2 text-base"
            rows={3}
            placeholder="e.g. 2 units water-damaged in Box 1 — QA hold"
            disabled={flagSaving}
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              disabled={flagSaving}
              onClick={() => setFlagOpen(false)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-base hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!flagText.trim() || flagSaving || !po.poDbId}
              onClick={submitFlag}
              className="rounded-md bg-gray-900 px-3 py-1.5 text-base font-medium text-white disabled:opacity-50"
            >
              {flagSaving ? 'Saving…' : 'Flag it'}
            </button>
          </div>
        </Modal>
      )}

      <ConfrimModal
        isOpen={canClose && closeConfirmOpen}
        title="Are you sure you want to set CLOSED status for this PO?"
        confirmButtonTitle="Close receipt"
        isLoading={closeBusy}
        size="max-w-md"
        submitClick={async () => {
          if (!canClose) return;
          setCloseBusy(true);
          try {
            await closeReceipt([po.id]);
            toast.success('Receipt closed', { position: 'bottom-right' });
            setCloseConfirmOpen(false);
          } finally {
            setCloseBusy(false);
          }
        }}
        cancelClick={() => {
          if (closeBusy) return;
          setCloseConfirmOpen(false);
        }}
      />
    </div>
  );
}

function Modal({ title, children, onClose, widthClassName = 'max-w-sm' }) {
  return (
    <div className="fixed inset-0 z-[125]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className={`w-full rounded-lg bg-white p-5 shadow-xl ${widthClassName}`}>
          <div className="mb-3 flex items-start justify-between gap-3">
            <h4 className="text-base font-semibold text-gray-900">{title}</h4>
            <button type="button" onClick={onClose} className="rounded p-0.5 text-gray-400 hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default PoInspector;
